import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, PackageX, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { SHIPPING_METHODS } from "@/constants/workflow";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const NO_CODE = "__none__";
const NO_PALLET = "__none__";

/** Edit a whole shipment: code, method, shipping date and the pallet of each panel. */
export function ShipmentEditDialog({
  projectId,
  shipment,
  pallets,
  open,
  onOpenChange,
  onDone,
}) {
  const [panels, setPanels] = useState(null);
  const [code, setCode] = useState("");
  const [method, setMethod] = useState("");
  const [shippedOn, setShippedOn] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const key = shipment?.shipment_code || NO_CODE;

  const loadPanels = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/projects/${projectId}/shipments/${encodeURIComponent(key)}/panels`
      );
      setPanels(data.panels);
    } catch (error) {
      toast.error(apiError(error));
    }
  }, [projectId, key]);

  useEffect(() => {
    if (!open || !shipment) return;
    setPanels(null);
    setSelected([]);
    setCode(shipment.shipment_code || "");
    setMethod(shipment.shipping_method || "");
    setShippedOn((shipment.shipped_at || "").slice(0, 10));
    loadPanels();
  }, [open, shipment, loadPanels]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onDone?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  const saveHeader = () =>
    run(async () => {
      const { data } = await api.patch(
        `/projects/${projectId}/shipments/${encodeURIComponent(key)}`,
        {
          shipment_code: code.trim() || null,
          shipping_method: method || null,
          shipped_on: shippedOn || null,
        }
      );
      toast.success(`Shipment updated · ${data.updated} panel(s)`);
      onOpenChange(false);
    });

  const assignPallet = (palletId, uids) =>
    run(async () => {
      await api.put(`/projects/${projectId}/pallets/assign`, {
        pallet_id: palletId === NO_PALLET ? null : palletId,
        panel_uids: uids,
      });
      toast.success(palletId === NO_PALLET ? "Removed from pallet" : "Pallet updated");
      setSelected([]);
      await loadPanels();
    });

  const toggle = (uid) =>
    setSelected((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="shipment-edit-dialog"
        className="max-w-2xl border-slate-200 bg-white text-slate-800"
      >
        <DialogHeader>
          <DialogTitle className="mono text-lg tracking-tight">
            Edit shipment {shipment?.shipment_code || "(no code)"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Shipment code
            </p>
            <input
              data-testid="shipment-code-edit-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="SHP-001"
              className="mono h-9 w-full rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-xs"
            />
          </div>
          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Shipping method
            </p>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger
                data-testid="shipment-method-select"
                className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
              >
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                {SHIPPING_METHODS.map((value) => (
                  <SelectItem key={value} value={value} className="text-xs">
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Shipping date
            </p>
            <input
              data-testid="shipment-date-input"
              type="date"
              value={shippedOn}
              onChange={(e) => setShippedOn(e.target.value)}
              className="mono h-9 w-full rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-xs"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-200 pt-3">
          <p className="mono text-[10.5px] uppercase tracking-wider text-slate-500">
            {panels ? `${panels.length} panel(s)` : "loading…"}
            {selected.length ? ` · ${selected.length} selected` : ""}
          </p>
          {selected.length ? (
            <Select onValueChange={(value) => assignPallet(value, selected)}>
              <SelectTrigger
                data-testid="shipment-bulk-pallet-select"
                className="h-8 w-56 border-slate-200 bg-[#EEF2F8] text-[11px]"
              >
                <SelectValue placeholder={`Move ${selected.length} panel(s) to pallet`} />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                <SelectItem value={NO_PALLET} className="text-xs">
                  — remove from pallet —
                </SelectItem>
                {pallets.map((pallet) => (
                  <SelectItem key={pallet.id} value={pallet.id} className="mono text-xs">
                    {pallet.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>

        {panels === null ? (
          <div className="grid h-24 place-items-center">
            <Loader2 size={18} className="animate-spin text-cyan-600" />
          </div>
        ) : (
          <div className="okn-scroll max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200">
            {panels.map((panel) => (
              <div
                key={panel.panel_uid}
                data-testid={`shipment-panel-row-${panel.panel_name}`}
                className="flex items-center gap-2 px-3 py-2"
              >
                <input
                  type="checkbox"
                  data-testid={`shipment-panel-check-${panel.panel_name}`}
                  checked={selected.includes(panel.panel_uid)}
                  onChange={() => toggle(panel.panel_uid)}
                  className="h-3.5 w-3.5 accent-cyan-600"
                />
                <span className="mono min-w-0 flex-1 truncate text-[11.5px] text-slate-800">
                  {panel.panel_name}
                </span>
                <span
                  className={`mono rounded px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider ${
                    panel.logistics === "Arrived"
                      ? "bg-emerald-500/10 text-emerald-700"
                      : "bg-cyan-500/10 text-cyan-700"
                  }`}
                >
                  {panel.logistics}
                </span>
                <Select
                  value={panel.pallet_id || NO_PALLET}
                  onValueChange={(value) => assignPallet(value, [panel.panel_uid])}
                >
                  <SelectTrigger
                    data-testid={`shipment-panel-pallet-${panel.panel_name}`}
                    className="h-8 w-40 border-slate-200 bg-[#EEF2F8] text-[11px]"
                  >
                    <SelectValue placeholder="No pallet" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-200 bg-white text-slate-800">
                    <SelectItem value={NO_PALLET} className="text-xs">
                      — no pallet —
                    </SelectItem>
                    {pallets.map((pallet) => (
                      <SelectItem key={pallet.id} value={pallet.id} className="mono text-xs">
                        {pallet.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {panel.pallet_id ? (
                  <button
                    data-testid={`shipment-panel-unpallet-${panel.panel_name}`}
                    title="Remove from pallet"
                    onClick={() => assignPallet(NO_PALLET, [panel.panel_uid])}
                    className="grid h-7 w-7 place-items-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <PackageX size={13} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}

        <Button
          data-testid="shipment-save-button"
          onClick={saveHeader}
          disabled={busy || panels === null}
          className="h-10 rounded-full bg-cyan-600 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500 disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} className="mr-2" />}
          Save shipment
        </Button>
      </DialogContent>
    </Dialog>
  );
}
